import { Eye, PencilLine, Trash2, Star, RotateCcw } from 'lucide-react'
import type { ProductCardVM } from '../../types/product'
import { formatCurrency } from '../../utils/currency'
import { chipClass, cn, iconButtonClass, surfaceClass } from '../../utils/theme'
import { getImageUrl } from '../../utils/url'

interface ProductCardProps {
  product: ProductCardVM
  onView: (productId: string | number) => void
  onEdit: (productId: string | number) => void
  onDelete: (productId: string | number) => void
  onRestore?: (productId: string | number) => void
  highlight?: string
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function HighlightedText({
  text,
  highlight,
}: {
  text: string
  highlight?: string
}) {
  if (!highlight) {
    return <>{text}</>
  }

  const safeHighlight = escapeRegExp(highlight)
  const regex = new RegExp(`(${safeHighlight})`, 'ig')
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <mark
            key={`${part}-${index}`}
            className="rounded bg-yellow-200 px-0.5 py-0 text-neutral-900 dark:bg-yellow-400/30"
          >
            {part}
          </mark>
        ) : (
          <span key={`${part}-${index}`}>{part}</span>
        ),
      )}
    </>
  )
}

export function ProductCard({
  product,
  onView,
  onEdit,
  onDelete,
  onRestore,
  highlight,
}: ProductCardProps) {
  const { id, name, description, price, categoryLabel, isActive, isFeatured, imageUrl, ingredients, deletedAt } = product
  const imageSrc = getImageUrl(imageUrl)
  const hasDescription = description.trim().length > 0
  const descriptionNode = hasDescription ? (
    <p className="text-sm text-neutral-500 dark:text-neutral-400">
      <HighlightedText text={description} highlight={highlight} />
    </p>
  ) : null

  return (
    <article
      className={cn(
        surfaceClass('card'),
        'group relative flex h-full flex-col overflow-hidden rounded-xl transition-shadow hover:shadow-md',
      )}
    >
      {imageUrl ? (
        <div className="relative h-40 w-full overflow-hidden">
          <img
            src={imageSrc}
            alt={`Imagen de ${name}`}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 text-sm text-white">
            <span>{categoryLabel}</span>
          </div>
        </div>
      ) : (
        <div className="flex h-14 items-center justify-between px-4 pt-4">
          <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{categoryLabel}</span>
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {formatCurrency(price)}
          </span>
        </div>
      )}
      <div className="flex flex-1 flex-col gap-3 px-4 pb-4 pt-2">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
              <HighlightedText text={name} highlight={highlight} />
            </h3>
            {imageUrl ? (
              <p className="text-sm text-neutral-200">
                {formatCurrency(price)}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5">
            {isFeatured && (
              <span className="flex items-center gap-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 px-2 py-0.5 text-xs font-semibold">
                <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                Destacado
              </span>
            )}
            {deletedAt ? (
              <span className="rounded bg-rose-100 dark:bg-rose-950/30 text-rose-700 dark:text-rose-450 px-2 py-0.5 text-xs font-semibold border border-rose-200 dark:border-rose-900/50">
                Eliminado
              </span>
            ) : (
              <span className={chipClass(isActive)}>{isActive ? 'Activo' : 'Inactivo'}</span>
            )}
          </div>
        </div>
        {descriptionNode}
        {ingredients.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
              Ingredientes
            </h4>
            <p className="mt-1 line-clamp-2 text-xs text-neutral-500 dark:text-neutral-400">
              {ingredients.join(', ')}
            </p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 dark:border-neutral-800">
        <button
          type="button"
          onClick={() => onView(id)}
          className={iconButtonClass()}
          aria-label={`Ver ${name}`}
        >
          <Eye className="h-5 w-5" />
        </button>
        {deletedAt ? (
          <button
            type="button"
            onClick={() => onRestore && onRestore(id)}
            className={cn(iconButtonClass(), 'text-blue-500 hover:text-blue-600 dark:text-blue-400')}
            title="Restaurar producto"
            aria-label={`Restaurar ${name}`}
          >
            <RotateCcw className="h-5 w-5" />
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onEdit(id)}
              className={iconButtonClass()}
              aria-label={`Editar ${name}`}
            >
              <PencilLine className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(id)}
              className={cn(iconButtonClass(), 'text-rose-500 hover:text-rose-600 dark:text-rose-300')}
              aria-label={`Eliminar ${name}`}
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
    </article>
  )
}

export default ProductCard
